import {
  Spinner,
  IconProps,
  Icon,
  PillBadgeColors,
  PillBadge,
  Dropdown,
  DropdownItem,
} from '@platform/components';
import { ReactNode } from 'react';
import { Link } from 'wouter';

type ReviewPageProps = {
  title: string;
  badge?: HeaderBadgeProps;
  headerIconItems?: ReviewPageIconItem[];
  headerLinks?: ReviewPageLink[];
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  children: React.ReactNode;
  backNavigation?: {
    label: string;
    href: string;
  };
  pageActions?: AdminViewPageAction[];
};

/**
 * Wrapper for admin pages requiring review action, handles loading and error states and scroll to top
 */
export const ReviewPageWrapper = (props: ReviewPageProps) => {
  const {
    title,
    headerIconItems,
    headerLinks,
    isLoading,
    isError,
    isSuccess,
    children,
    backNavigation,
    pageActions,
  } = props;

  return (
    <div>
      {isLoading && (
        <div className='flex justify-center'>
          <Spinner />
        </div>
      )}
      {isError && <div>Error loading data</div>}
      {isSuccess && (
        <div className='flex justify-center'>
          <div className='w-[1000px] flex flex-col gap-2 border border-gray-100 px-6 py-2'>
            <div className='flex justify-between items-center'>
              {backNavigation && (
                <Link href={backNavigation.href} className='text-sm text-gray-500 hover:underline'>
                  {backNavigation.label}
                </Link>
              )}
              {pageActions && <PageActions actions={pageActions} />}
            </div>
            <div className='flex flex-col gap-2 justify-center items-center'>
              <h1 className='text-display-sm text-center'>{title}</h1>
              {props.badge && <ReviewPageBadge text={props.badge.text} color={props.badge.color} />}
            </div>
            {headerIconItems && <ReviewPageIconItems items={headerIconItems} />}
            {headerLinks && <ReviewPageLinks links={headerLinks} />}
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

export type AdminViewPageAction =
  | {
      type: 'link';
      text: string;
      href: string;
      isDanger?: boolean;
    }
  | {
      type: 'button';
      text: string;
      onPress: () => void;
      isDanger?: boolean;
    };

const PageActions = ({ actions }: { actions: AdminViewPageAction[] }) => {
  return (
    <Dropdown type='icon' iconName='dots-vertical' iconSize='md' ariaLabel='Page actions'>
      {actions.map((action, index) =>
        action.type === 'button' ?
          <DropdownItem
            key={index}
            onAction={() => action.onPress()}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            {action.text}
          </DropdownItem>
        : <DropdownItem
            key={index}
            href={action.href}
            additionalClassnames={action.isDanger ? 'text-error-600 font-semibold' : ''}
          >
            {action.text}
          </DropdownItem>,
      )}
    </Dropdown>
  );
};

type HeaderBadgeProps = {
  text: string;
  color: PillBadgeColors;
};

const ReviewPageBadge = (props: HeaderBadgeProps) => {
  const { text, color } = props;
  return (
    <PillBadge variant='plain' color={color} size='sm'>
      {text}
    </PillBadge>
  );
};

export type ReviewPageIconItem = {
  icon: IconProps['type'];
  label: string;
  value?: string | number | ReactNode | null;
};

export const ReviewPageIconItems = ({ items }: { items: ReviewPageIconItem[] }) => {
  return (
    <div className='md:flex justify-center gap-4 md:mx-auto'>
      {items.map((item) => (
        <div key={`${item.value}-${item.icon}`} className='flex items-center'>
          <span className='sr-only'>{item.label}:</span>
          <Icon type={item.icon} additionalClassNames='mr-2' />
          {item.value ?? 'Not provided'}
        </div>
      ))}
    </div>
  );
};

type ReviewPageLink = {
  label: string;
  url?: string | null;
};

const ReviewPageLink = ({ link }: { link: ReviewPageLink }) => {
  return (
    <div className='flex items-center'>
      {link.url ?
        <a
          href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
          target='_blank'
          className='text-brand-500 hover:underline flex items-center text-sm'
        >
          {link.label} <Icon type='arrow-up-right' size='18' />
        </a>
      : <p className='text-sm'>{link.label} Not Provided</p>}
    </div>
  );
};

export const ReviewPageLinks = ({ links }: { links: ReviewPageLink[] }) => {
  return (
    <div className='md:flex justify-center gap-4 md:mx-auto'>
      {links.map((link) => (
        <ReviewPageLink key={`${link.label}-${link.url}`} link={link} />
      ))}
    </div>
  );
};

/**
 * Very basic formatting for review page items
 */
export const ReviewItem = ({ label, children }: { label: string; children: ReactNode }) => {
  return (
    <>
      <h2 className='text-xl font-semibold'>{label}:</h2>
      {children}
    </>
  );
};

export const ReviewItemShort = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | ReactNode | null;
}) => {
  return (
    <div>
      <span className='font-semibold text-xl mr-2'>{label}:</span>
      <span>{value ?? 'Not provided'}</span>
    </div>
  );
};
